import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import LanguageModal from '../components/LanguageModal';
import { useState } from 'react';
import SeoHead from '../components/SeoHead';
import { MUSIC_SEO } from '../seo/seoConfig';
import { MUSIC_GUIDES, guidePath } from '../content/guides/musicGuides';
import ga from '../services/analytics';
import { SketchPageShell, SketchNavBar, SketchCard } from '../components/sketchy';

const DocsHubPage = () => {
  const { t } = useTranslation();
  const [languageOpen, setLanguageOpen] = useState(false);

  useEffect(() => {
    ga.event('docs_page_viewed', { page: 'hub' });
  }, []);

  return (
    <SketchPageShell className="min-h-screen flex flex-col">
      <SeoHead meta={MUSIC_SEO.docs} />
      <SketchNavBar>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--color-hover)] transition-colors group"
              >
                <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="text-sm font-medium">{t('buttons.back')}</span>
              </Link>
              <Link to="/" className="text-lg font-bold text-[var(--color-text)] hover:text-[var(--brand-from)] transition-colors hidden sm:inline">
                cassette.diy
              </Link>
            </div>
            <button
              type="button"
              onClick={() => setLanguageOpen(true)}
              className="px-3 py-2 text-sm rounded-lg hover:bg-[var(--color-hover)] transition-colors"
            >
              🌐 {t('menu.language')}
            </button>
          </div>
        </div>
      </SketchNavBar>

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">{t('docs.hub.title')}</h1>
          <p className="text-[var(--color-muted)] mb-8">{t('docs.hub.intro')}</p>

          <div className="space-y-4">
            <Link to="/docs/mcp" className="block group">
              <SketchCard className="p-6 hover:border-[var(--brand-from)] transition-colors">
                <h2 className="text-xl font-semibold group-hover:text-[var(--brand-from)]">{t('docs.hub.mcpCardTitle')}</h2>
                <p className="text-sm text-[var(--color-muted)] mt-2">{t('docs.hub.mcpCardDescription')}</p>
              </SketchCard>
            </Link>

            <SketchCard className="p-6 opacity-60 cursor-not-allowed">
              <h2 className="text-xl font-semibold">{t('docs.hub.comingSoonTitle')}</h2>
              <p className="text-sm text-[var(--color-muted)] mt-2">{t('docs.hub.comingSoonDescription')}</p>
            </SketchCard>

            <div className="pt-2">
              <h2 className="text-lg font-semibold mb-3">Workflow guides</h2>
              <ul className="space-y-2">
                {MUSIC_GUIDES.map((guide) => (
                  <li key={guide.slug}>
                    <Link
                      to={guidePath(guide.slug)}
                      className="text-[var(--brand-from)] hover:underline"
                      onClick={() => ga.event('guide_link_click', { from: 'docs_hub', guide_slug: guide.slug })}
                    >
                      {guide.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-8 text-sm">
            <Link to="/help" className="text-[var(--brand-from)] hover:underline">
              {t('docs.hub.helpLink')}
            </Link>
          </p>
        </div>
      </main>

      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </SketchPageShell>
  );
};

export default DocsHubPage;
